import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FilterManagementState, FilterItem, DEFAULT_FILTER_ITEMS } from '@/types';

interface FilterManagementStore extends FilterManagementState {
  savedFilterState: FilterItem[];
  setFilterManagement: (state: FilterManagementState) => void;
  updateFilterItem: (id: string, updates: Partial<FilterItem>) => void;
  reorderFilters: (filters: FilterItem[]) => void;
  toggleFilterEditMode: () => void;
  saveFilterChanges: () => void;
  cancelFilterChanges: () => void;
  resetFiltersToDefault: () => void;
}

const PINNED_IDS = new Set(['statement', 'export']);

const pinFilterOrder = (filters: FilterItem[]): FilterItem[] => {
  const statement = filters.find(f => f.id === 'statement');
  const exportFilter = filters.find(f => f.id === 'export');
  const rest = filters.filter(f => !PINNED_IDS.has(f.id));
  const ordered: FilterItem[] = [];
  if (statement) ordered.push(statement);
  ordered.push(...rest);
  if (exportFilter) ordered.push(exportFilter);
  return ordered.map((f, i) => ({ ...f, order: i }));
};

const mergeFiltersWithDefaults = (storedFilters: FilterItem[]): FilterItem[] => {
  const storedIds = new Set(storedFilters.map(f => f.id));
  const newFilters = DEFAULT_FILTER_ITEMS.filter(f => !storedIds.has(f.id));
  const maxOrder = Math.max(...storedFilters.map(f => f.order), -1);
  const mergedFilters = [
    ...storedFilters,
    ...newFilters.map((filter, index) => ({
      ...filter,
      order: maxOrder + 1 + index,
    }))
  ];
  return pinFilterOrder(mergedFilters.sort((a, b) => a.order - b.order));
};

const getFilterManagementFromStorage = (): FilterManagementState => {
  try {
    const stored = localStorage.getItem('filterManagement');
    if (stored) {
      const parsed = JSON.parse(stored);
      const mergedFilters = mergeFiltersWithDefaults(parsed.filters || []);
      return {
        filters: mergedFilters,
        isEditMode: false,
        hasChanges: false,
      };
    }
  } catch {
    // Fallback to default if parsing fails
  }
  return {
    filters: DEFAULT_FILTER_ITEMS,
    isEditMode: false,
    hasChanges: false,
  };
};

const saveFilterManagementToStorage = (state: FilterManagementState): void => {
  try {
    localStorage.setItem('filterManagement', JSON.stringify({
      filters: state.filters,
    }));
  } catch {
    // Silently fail if localStorage is not available
  }
};

const areFilterStatesEqual = (state1: FilterItem[], state2: FilterItem[]): boolean => {
  if (state1.length !== state2.length) return false;
  
  return state1.every((filter1, index) => {
    const filter2 = state2[index];
    return filter1.id === filter2.id && 
           filter1.enabled === filter2.enabled && 
           filter1.order === filter2.order;
  });
};

export const useFilterManagementStore = create<FilterManagementStore>()(
  devtools(
    (set, _get) => ({
      ...getFilterManagementFromStorage(),
      savedFilterState: getFilterManagementFromStorage().filters,
      
      setFilterManagement: (state) => {
        saveFilterManagementToStorage(state);
        set(state);
      },
      
      updateFilterItem: (id, updates) => set((state) => {
        const newFilters = state.filters.map(filter => 
          filter.id === id ? { ...filter, ...updates } : filter
        );
        const hasChanges = !areFilterStatesEqual(newFilters, state.savedFilterState);
        return {
          filters: newFilters,
          hasChanges,
        };
      }),
      
      reorderFilters: (filters) => set((state) => {
        const reorderedFilters = pinFilterOrder(filters);
        const hasChanges = !areFilterStatesEqual(reorderedFilters, state.savedFilterState);
        return {
          filters: reorderedFilters,
          hasChanges,
        };
      }),
      
      toggleFilterEditMode: () => set((state) => ({
        isEditMode: !state.isEditMode,
        hasChanges: false,
      })),
      
      saveFilterChanges: () => set((state) => {
        const newState: FilterManagementState = {
          filters: state.filters,
          isEditMode: false,
          hasChanges: false,
        };
        saveFilterManagementToStorage(newState);
        return { 
          ...newState,
          savedFilterState: state.filters
        };
      }),
      
      cancelFilterChanges: () => {
        const storedState = getFilterManagementFromStorage();
        set({
          ...storedState,
          isEditMode: false,
          hasChanges: false,
          savedFilterState: storedState.filters
        });
      },
      
      resetFiltersToDefault: () => set((state) => {
        const hasChanges = !areFilterStatesEqual(DEFAULT_FILTER_ITEMS, state.savedFilterState);
        return {
          filters: DEFAULT_FILTER_ITEMS,
          hasChanges,
        };
      }),
    }),
    {
      name: 'filter-management-store', enabled: import.meta.env.DEV,
    }
  )
);

